import { FC } from 'react';
import { Box, Tooltip } from '@mui/material';
import { Tag } from '../Tag/tag.component';
import { OrderProductionStatusEnum, OrderStatusEnum } from '../../Services/api/orders/orders.interface';
import { normalizeOrderStatusColors, normalizeOrderStatusTitle } from '../../utils/normalizeOrderStatus';

interface IOrderStatusCell {
  status: OrderStatusEnum;
  productionStatus?: OrderProductionStatusEnum;
  errorMessage?: string;
}

const labelsOrderProductionStatus: Record<OrderProductionStatusEnum, string> = {
  [OrderProductionStatusEnum.STAND_BY]: 'Aguardando produção',
  [OrderProductionStatusEnum.PRODUCTION]: 'Em produção',
  [OrderProductionStatusEnum.HALF_FINISHED]: 'Parcialmente produzido',
  [OrderProductionStatusEnum.FINISHED]: 'Produzido',
};

const colorsOrderProductionStatus: Record<OrderProductionStatusEnum, { bgColor: string; textColor: string }> = {
  [OrderProductionStatusEnum.STAND_BY]: {
    bgColor: 'rgba(110, 197, 5, 0.73)',
    textColor: '#FFFFFF',
  },
  [OrderProductionStatusEnum.PRODUCTION]: {
    bgColor: 'rgba(223, 148, 27, 0.7)',
    textColor: '#FFFFFF',
  },
  [OrderProductionStatusEnum.HALF_FINISHED]: {
    bgColor: 'rgba(18, 120, 237, 0.7)',
    textColor: '#FFFFFF',
  },
  [OrderProductionStatusEnum.FINISHED]: {
    bgColor: 'rgba(40, 85, 135, 0.7)',
    textColor: '#FFFFFF',
  },
};

const tagBoxStyle = {
  width: '100%',
  minHeight: '32px',
  padding: '8px 10px',
  whiteSpace: 'normal',
  boxSizing: 'border-box',
};

const tagTitleStyle = {
  fontSize: '13px',
  width: '100%',
  textAlign: 'center',
  whiteSpace: 'normal',
  boxSizing: 'border-box',
  lineHeight: 1.2,
};

export const OrderStatusCell: FC<IOrderStatusCell> = ({
  status,
  productionStatus,
  errorMessage,
}) => {
  const orderColors = normalizeOrderStatusColors(status);
  const productionColors = productionStatus
    ? colorsOrderProductionStatus[productionStatus]
    : { bgColor: 'rgba(158, 158, 158, 0.7)', textColor: '#212121' };

  const orderTag = (
    <Tag
      title={`Pedido: ${normalizeOrderStatusTitle(status) || status || 'Nao informado'}`}
      boxStyle={{
        ...tagBoxStyle,
        backgroundColor: orderColors.bgColor,
      }}
      titleStyle={{
        ...tagTitleStyle,
        color: orderColors.textColor,
      }}
    />
  );

  const productionTag = (
    <Tag
      title={`Produção: ${productionStatus ? labelsOrderProductionStatus[productionStatus] : 'Nao informado'}`}
      boxStyle={{
        ...tagBoxStyle,
        backgroundColor: productionColors.bgColor,
      }}
      titleStyle={{
        ...tagTitleStyle,
        color: productionColors.textColor,
      }}
    />
  );

  const shouldShowErrorTooltip = [
    OrderStatusEnum.DEAD,
    OrderStatusEnum.INVOICE_PROBLEM,
  ].includes(status);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'stretch',
        flexDirection: 'column',
        gap: '8px',
        py: 1,
        px: 1,
      }}
    >
      {shouldShowErrorTooltip ? (
        <Tooltip
          title={errorMessage || 'Motivo nao informado'}
          placement="top"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'red',
                color: 'white',
                fontSize: '16px',
                fontWeight: 'bold',
                '& .MuiTooltip-arrow': {
                  color: 'red',
                },
              },
            },
          }}
        >
          <Box sx={{ width: '100%', cursor: 'pointer' }}>
            {orderTag}
          </Box>
        </Tooltip>
      ) : (
        orderTag
      )}

      {productionTag}
    </Box>
  );
};

export default OrderStatusCell;
